import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle, Button, Card } from '@bookaburra/ui';
import { CheckCircle2, CreditCard, Zap } from 'lucide-react';
import {
  BillingApiError,
  fetchBillingInfo,
  startCheckout,
  openBillingPortal,
  type BillingTier,
} from '../booking-flow/billing-api';
import { fetchBookingTenant } from '../booking-flow/api';
import { useDocumentTitle } from '../lib/use-document-title';
interface BillingSearch {
  upgraded?: boolean;
  topup?: boolean;
}

function isTrue(v: unknown): boolean {
  return v === '1' || v === 'true' || v === true;
}

export const Route = createFileRoute('/admin/$slug/billing')({
  validateSearch: (s: Record<string, unknown>): BillingSearch => {
    const result: BillingSearch = {};
    if (isTrue(s['upgraded'])) result.upgraded = true;
    if (isTrue(s['topup'])) result.topup = true;
    return result;
  },
  component: BillingPage,
});

const PLAN_COPY = {
  small: {
    name: 'Small business',
    price: 'A$29',
    cadence: '/month',
    bullets: [
      'Up to 20 team members',
      'Up to 300 bookings a month',
      'Up to 200 SMS / month (booking confirmation + 24h reminder only)',
      'Booking chatbot',
      'AI voice agent via booking site',
    ],
  },
  large: {
    name: 'Large business',
    price: 'A$159',
    cadence: '/month',
    bullets: [
      'Up to 100 team members',
      'Up to 2,000 bookings a month',
      'Up to 1,000 SMS / month + buy more credits any time',
      'All SMS and email notification types',
      'Enable/disable any notification channel',
      'Waiting list when slots run out',
      'Sick-day booking reshuffle',
      'Customise email and SMS wording',
    ],
  },
} as const;

function tierLabel(tier: BillingTier): string {
  if (tier === 'small') return 'Small business';
  if (tier === 'large') return 'Large business';
  return 'Free';
}

function BillingPage() {
  const { slug } = Route.useParams();
  const search = useSearch({ from: '/admin/$slug/billing' });
  const qc = useQueryClient();

  const billingQ = useQuery({
    queryKey: ['billing-info', slug],
    queryFn: () => fetchBillingInfo(slug),
  });

  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });

  useDocumentTitle(
    tenantQ.data ? `Billing · ${tenantQ.data.tenant.name} Admin` : 'Billing',
  );

  const checkoutMut = useMutation({
    mutationFn: (plan: Parameters<typeof startCheckout>[1]) =>
      startCheckout(slug, plan),
  });

  const portalMut = useMutation({
    mutationFn: () => openBillingPortal(slug),
  });

  const billing = billingQ.data;
  const tier = billing?.tier ?? 'free';
  const isPaid = tier !== 'free';

  return (
    <main className="mx-auto max-w-4xl px-6 py-10 sm:px-8">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-primary/10 p-2 text-primary">
            <CreditCard className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Billing</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage your plan and SMS credits.
            </p>
          </div>
        </div>
        <a
          href="https://bookaburra.com/pricing"
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          Compare plans →
        </a>
      </header>

      {/* Success banners */}
      {search.upgraded ? (
        <Alert className="mb-6 border-green-500/30 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle>You're all set!</AlertTitle>
          <AlertDescription>
            Welcome to {tierLabel(tier)}. Your new features are active now.
          </AlertDescription>
        </Alert>
      ) : null}

      {search.topup ? (
        <Alert className="mb-6 border-green-500/30 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle>SMS credits added</AlertTitle>
          <AlertDescription>
            Your top-up credits have been added to your balance.
          </AlertDescription>
        </Alert>
      ) : null}

      {billingQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : billingQ.error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load billing</AlertTitle>
          <AlertDescription>
            {billingQ.error instanceof BillingApiError
              ? billingQ.error.code
              : 'Unknown error'}
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-8">
          {/* Current plan card — always shown */}
          {isPaid ? (
            <PaidPlanCard
              tier={tier}
              tierValidUntil={billing?.tierValidUntil ?? null}
              onManage={() => portalMut.mutate()}
              managing={portalMut.isPending}
              error={
                portalMut.error instanceof BillingApiError
                  ? portalMut.error.code
                  : portalMut.error
                  ? 'unknown_error'
                  : null
              }
            />
          ) : (
            <FreePlanCard />
          )}

          {/* Upgrade cards (free tenants see both; small sees Large only) */}
          {tier !== 'large' ? (
            <section>
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                {tier === 'free' ? 'Upgrade your plan' : 'Upgrade'}
              </h2>
              <div className="grid gap-5 sm:grid-cols-2">
                {tier === 'free' ? (
                  <PlanUpgradeCard
                    plan="small"
                    onSubscribe={() => checkoutMut.mutate('small')}
                    loading={checkoutMut.isPending && checkoutMut.variables === 'small'}
                  />
                ) : null}
                <PlanUpgradeCard
                  plan="large"
                  featured
                  onSubscribe={() => checkoutMut.mutate('large')}
                  loading={checkoutMut.isPending && checkoutMut.variables === 'large'}
                />
              </div>
              {checkoutMut.error ? (
                <Alert variant="destructive" className="mt-4">
                  <AlertTitle>Couldn't open checkout</AlertTitle>
                  <AlertDescription>
                    {checkoutMut.error instanceof BillingApiError
                      ? checkoutMut.error.code
                      : 'Unknown error'}
                  </AlertDescription>
                </Alert>
              ) : null}
            </section>
          ) : null}

          {/* SMS top-up (paid tenants only) */}
          {isPaid ? (
            <SmsTopupPanel
              slug={slug}
              tier={tier}
              balance={billing?.smsTopUpBalance ?? 0}
              onBuy={(plan) => checkoutMut.mutate(plan)}
              loading={checkoutMut.isPending}
              error={
                checkoutMut.error instanceof BillingApiError
                  ? checkoutMut.error.code
                  : checkoutMut.error
                  ? 'unknown_error'
                  : null
              }
            />
          ) : null}
        </div>
      )}
    </main>
  );
}

function FreePlanCard() {
  return (
    <Card className="border-border/50 p-6">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        Current plan
      </p>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
        <span className="text-2xl font-bold">Free</span>
        <span className="text-sm text-muted-foreground">no charge</span>
      </div>
      <ul className="mt-4 space-y-1 text-sm text-muted-foreground">
        <li>· Up to 3 team members</li>
        <li>· Up to 50 bookings a month</li>
        <li>· Email confirmations and reminders</li>
        <li>· No SMS, no waiting list, no chatbot or voice agent</li>
      </ul>
    </Card>
  );
}

function PaidPlanCard({
  tier,
  tierValidUntil,
  onManage,
  managing,
  error,
}: {
  tier: BillingTier;
  tierValidUntil: number | null;
  onManage: () => void;
  managing: boolean;
  error: string | null;
}) {
  const copy = tier !== 'free' ? PLAN_COPY[tier] : null;

  return (
    <Card className="border-primary/30 bg-primary/5 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">
            Current plan
          </p>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
            <span className="text-2xl font-bold">{copy?.name ?? 'Free'}</span>
            {copy ? (
              <span className="text-sm text-muted-foreground">
                {copy.price}{copy.cadence}
              </span>
            ) : null}
          </div>
          {tierValidUntil ? (
            <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">
              Active until{' '}
              {new Date(tierValidUntil).toLocaleDateString('en-AU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
              {' '}— your plan will change at the end of the billing period.
            </p>
          ) : null}
        </div>
        <Button
          variant="outline"
          onClick={onManage}
          disabled={managing}
        >
          {managing ? 'Opening…' : 'Manage billing'}
        </Button>
      </div>
      {error ? (
        <p className="mt-3 text-sm text-destructive">{error}</p>
      ) : null}
      <div className="mt-4 border-t border-border/50 pt-4">
        <p className="mb-2 text-sm font-medium">Via the billing portal you can:</p>
        <ul className="space-y-1 text-sm text-muted-foreground">
          <li>· Upgrade or downgrade your plan (e.g. Large → Small)</li>
          <li>· Cancel your subscription — unused days are refunded immediately and your plan moves to Free</li>
          <li>· Update your payment method</li>
          <li>· View past invoices</li>
        </ul>
      </div>
    </Card>
  );
}

function PlanUpgradeCard({
  plan,
  featured = false,
  onSubscribe,
  loading,
}: {
  plan: 'small' | 'large';
  featured?: boolean;
  onSubscribe: () => void;
  loading: boolean;
}) {
  const copy = PLAN_COPY[plan];
  return (
    <Card
      className={`flex flex-col p-6 ${
        featured ? 'border-primary/40 ring-1 ring-primary/20' : ''
      }`}
    >
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">
          {copy.name}
        </p>
        <div className="mt-1 flex items-baseline gap-1">
          <span className="text-3xl font-bold">{copy.price}</span>
          <span className="text-sm text-muted-foreground">{copy.cadence}</span>
        </div>
      </div>
      <ul className="mt-5 flex-1 space-y-2 text-sm text-muted-foreground">
        {copy.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
            {b}
          </li>
        ))}
      </ul>
      <Button
        className="mt-6 w-full"
        variant={featured ? 'primary' : 'outline'}
        onClick={onSubscribe}
        disabled={loading}
      >
        {loading ? 'Opening checkout…' : `Subscribe — ${copy.price}${copy.cadence}`}
      </Button>
    </Card>
  );
}

const SMS_INCLUDED: Record<'small' | 'large', string> = {
  small: '200 SMS / month',
  large: '1,000 SMS / month',
};

function SmsTopupPanel({
  slug,
  tier,
  balance,
  onBuy,
  loading,
  error,
}: {
  slug: string;
  tier: BillingTier;
  balance: number;
  onBuy: (plan: 'topup_100' | 'topup_500') => void;
  loading: boolean;
  error: string | null;
}) {
  const isLow = balance > 0 && balance < 50;
  const isZero = balance === 0;

  return (
    <section>
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
        SMS credits
      </h2>
      <Card className="p-6">
        <div className="grid gap-6 sm:grid-cols-2">
          {/* Plan-included SMS */}
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-muted p-2 text-muted-foreground">
              <Zap className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Included with your plan
              </p>
              <p className="mt-1 text-xl font-bold">
                {tier !== 'free' ? SMS_INCLUDED[tier] : '—'}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">resets each month</p>
            </div>
          </div>

          {/* Top-up balance */}
          <div className="flex items-start gap-3">
            <div className={`rounded-xl p-2 ${isZero ? 'bg-muted text-muted-foreground' : isLow ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400' : 'bg-primary/10 text-primary'}`}>
              <Zap className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Top-up credits remaining
              </p>
              <p className={`mt-1 text-xl font-bold ${isLow ? 'text-amber-700 dark:text-amber-400' : ''}`}>
                {balance.toLocaleString()}
              </p>
              {isLow ? (
                <p className="mt-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                  Running low — top up soon
                </p>
              ) : isZero ? (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Used once your monthly plan SMS run out
                </p>
              ) : (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Used once your monthly plan SMS run out
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 border-t border-border/50 pt-5">
          <Button
            variant="outline"
            onClick={() => onBuy('topup_100')}
            disabled={loading}
          >
            Buy 100 SMS credits
          </Button>
          <Button
            onClick={() => onBuy('topup_500')}
            disabled={loading}
          >
            Buy 500 SMS credits
          </Button>
        </div>
        {error ? (
          <p className="mt-3 text-sm text-destructive">{error}</p>
        ) : null}
        <p className="mt-4 text-xs text-muted-foreground">
          Top-up credits never expire and carry over if you cancel and resubscribe.
        </p>
        {tier === 'large' ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Tip: you can{' '}
            <Link
              to="/admin/$slug/templates"
              params={{ slug }}
              className="underline underline-offset-2 hover:text-foreground"
            >
              disable notification types you don&rsquo;t need
            </Link>{' '}
            to reduce how many SMS credits you use each month.{' '}
            <a
              href="https://bookaburra.com/notifications"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-foreground"
            >
              Preview all notification templates &rarr;
            </a>
          </p>
        ) : null}
      </Card>
    </section>
  );
}
